/* ============================================================================
   WAKA live inventory — market-specific stock status + CTA on the PDP.

   Ports the Venue inventory-status logic onto the new theme's buy panel.
   Calls getInventoryStatus (Firebase) with the selected variant + market, then:
     • refines the #WakaStock line with the live status,
     • localises the "check our retailers" list by market,
     • drives the buy CTA: Add to cart  /  Pre-order  /  Notify me,
     • paints the per-size status dots.

   CTA decision (market-aware):
     in-stock | low-inventory | almost-gone        -> "Add to cart"
     out-of-stock + a pre-order window is coming    -> "Pre-order"
     out-of-stock + no more shipments coming        -> "Notify me"
   "No more shipments" = the server returns no pre-order window for this market,
   OR the variant is flagged no-restock (variant metafield waka.no_restock=true,
   surfaced as data-no-restock on the size button).

   waka-product.js owns price/specs/total and dispatches `waka:variant`; this
   module listens and re-fetches, and is the AUTHORITATIVE source for the CTA
   mode (waka-product.js refreshTotal() defers to pdp.dataset.ctaMode).

   Endpoint + retailers can be overridden via window.WAKA_INVENTORY_DATA.
   NOTE: pre-order ship windows come from the server — it follows the
   "Early/Mid/Late <Month>" rule (never an exact customer-facing date).
   ============================================================================ */
(function () {
  var CFG = window.WAKA_INVENTORY_DATA || {};
  var API = CFG.apiUrl || 'https://us-central1-waka-agent.cloudfunctions.net/getInventoryStatus';

  // Retailers by market. US retailers must NOT leak into other markets' panels.
  // CA = 30 Noeuds per Nigel (2026-06-07); storefront URL supplied by Nigel
  // 2026-07-07. ANZ + EU/UK are direct-only (no retailers).
  var RETAILERS_BY_MARKET = CFG.retailers || {
    'us': [
      { name: 'MACkite Boardsports',    url: 'https://www.mackiteboarding.com/2025-waka-glide-v1-foil-board/' },
      { name: 'Green Hat Kiteboarding', url: 'https://greenhatkiteboarding.com/products/waka-glide-folding-foilboard' }
    ],
    'default':        [
      { name: 'MACkite Boardsports',    url: 'https://www.mackiteboarding.com/2025-waka-glide-v1-foil-board/' },
      { name: 'Green Hat Kiteboarding', url: 'https://greenhatkiteboarding.com/products/waka-glide-folding-foilboard' }
    ],
    'ca':             [ { name: '30 Noeuds', url: 'https://30noeuds.com/products/waka-glide-folding-foil-board' } ],
    'canada':         [ { name: '30 Noeuds', url: 'https://30noeuds.com/products/waka-glide-folding-foil-board' } ],
    'australia':      [],
    'new-zealand':    [],
    'eu':             [],
    'europe':         [],
    'uk':             [],
    'gb':             [],
    'united-kingdom': []
  };

  var root = document.getElementById('WakaPdp');
  if (!root) return;
  var stock = document.getElementById('WakaStock');
  var retailers = document.getElementById('WakaRetailers');
  var conf = document.getElementById('WakaConfidence');
  var form = document.getElementById('WakaProductForm');
  var ctaLabel = root.querySelector('[data-cta-label]');
  var ctaTotal = root.querySelector('[data-cta-total]');
  var market = (root.getAttribute('data-market') || 'us').toLowerCase();

  function retailersForMarket() {
    return RETAILERS_BY_MARKET[market] || RETAILERS_BY_MARKET['default'] || [];
  }
  function esc(s){ return String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;'); }

  function fetchStatus(variantId) {
    if (!variantId) return Promise.resolve(null);
    return fetch(API + '?variantId=' + encodeURIComponent(variantId) + '&market=' + encodeURIComponent(market))
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  // Look up the size button for a variant to read its no-restock flag + label.
  function sizeBtnFor(variantId){
    var found = null;
    Array.prototype.forEach.call(root.querySelectorAll('.size'), function(b){
      if (String(b.getAttribute('data-variant-id')) === String(variantId)) found = b;
    });
    return found;
  }
  function sizeLabel(btn){
    if (!btn) { var a = root.querySelector('.size.active'); btn = a; }
    if (!btn) return '';
    return (btn.getAttribute('data-size') || btn.textContent || '').trim().split('\n')[0].trim();
  }

  // ---- CTA control. mode = 'add' | 'preorder' | 'notify' --------------------
  function applyCta(mode){
    root.dataset.ctaMode = mode;
    if (!ctaLabel) return;
    if (mode === 'notify'){
      ctaLabel.firstChild.textContent = 'Notify me when available';
      if (ctaTotal) ctaTotal.style.display = 'none';
    } else {
      if (ctaTotal) ctaTotal.style.display = '';
      ctaLabel.firstChild.textContent = (mode === 'preorder' ? 'Pre-order' : 'Add to cart') + ' · ';
    }
  }

  // Intercept submit when in notify mode → open Viento instead of add-to-cart.
  if (form){
    form.addEventListener('submit', function(e){
      if (root.dataset.ctaMode !== 'notify') return;
      e.preventDefault();
      var sz = sizeLabel(root.querySelector('.size.active'));
      var pname = (root.getAttribute('data-product-title') || 'WAKA Glide').trim();
      var prompt = 'Notify me when the ' + pname + (sz ? ' ' + sz : '') + ' is back in stock';
      if (window.Viento && typeof window.Viento.ask === 'function') window.Viento.ask(prompt);
      else if (window.Viento && typeof window.Viento.open === 'function') window.Viento.open();
    });
  }

  function renderRetailers(intro){
    if (!retailers) return;
    var list = retailersForMarket();
    if (!list.length){ retailers.innerHTML = ''; return; }
    var links = list.map(function (r) {
      return r.url
        ? '<a href="' + esc(r.url) + '" target="_blank" rel="noopener noreferrer">' + esc(r.name) + '</a>'
        : '<span class="wr-name">' + esc(r.name) + '</span>';
    }).join(' or ');
    var html = '<div class="waka-retailers">';
    if (intro) html += '<p class="wr-pre">' + intro + '</p>';
    html += '<p class="wr-rtl"><b>Or check with our retailers:</b><br>' + links + '</p></div>';
    retailers.innerHTML = html;
  }

  function render(data, variantId) {
    if (!stock || !data) return;
    var status = data.status;
    var pre = data.preorder;
    var btn = sizeBtnFor(variantId);
    var noRestock = !!(btn && btn.getAttribute('data-no-restock') === 'true');
    var hasWindow = !!(pre && (pre.nextShipment || pre.message));

    if (retailers) retailers.innerHTML = '';
    stock.classList.remove('pre', 'low', 'sold');

    if (status === 'in-stock') {
      stock.innerHTML = '<span class="sdot"></span><span><b>In stock</b> · ships now</span>';
      applyCta('add');
    } else if (status === 'low-inventory') {
      stock.classList.add('low');
      stock.innerHTML = '<span class="sdot"></span><span><b>Low inventory</b> · ships now</span>';
      applyCta('add');
    } else if (status === 'almost-gone') {
      stock.classList.add('low');
      stock.innerHTML = '<span class="sdot"></span><span><b>Almost gone</b> · order soon</span>';
      applyCta('add');
    } else if (status === 'out-of-stock') {
      stock.classList.add('sold');
      if (hasWindow && !noRestock) {
        // Pre-order — more stock is on the way for this market.
        var ship = pre && pre.nextShipment ? pre.nextShipment : (stock.getAttribute('data-shipdate') || '');
        var preNote = stock.getAttribute('data-preorder-note') || '';
        stock.innerHTML = '<span class="sdot"></span><span><b>Pre-order</b>' + (preNote ? ' · ' + esc(preNote) : '') + (ship ? ' · ' + esc(ship) : '') + ' · pre-orders ship first</span>';
        applyCta('preorder');
        if (conf) conf.textContent = conf.getAttribute('data-preorder') || 'Pre-orders ship first · 30-day returns';
        renderRetailers(pre && pre.message ? '<b>Pre-order available</b><br>' + esc(pre.message) : '');
      } else {
        // Sold out, no more shipments coming → Notify, not pre-order.
        stock.innerHTML = '<span class="sdot"></span><span><b>Sold out</b> · no more shipments of this size</span>';
        applyCta('notify');
        if (conf) conf.textContent = 'Get notified the moment this size is back';
        renderRetailers('<b>Want one now?</b><br>It may still be in stock with a retailer:');
      }
    }
  }

  function update(variantId) { fetchStatus(variantId).then(function(d){ render(d, variantId); }); }

  // initial
  var vid = document.getElementById('WakaVariantId');
  if (vid && vid.value) update(vid.value);

  // on size change (dispatched by waka-product.js)
  document.addEventListener('waka:variant', function (e) {
    var id = e.detail && e.detail.variantId;
    if (id) update(id);
  });

  /* ---- per-size status dots (green=in · amber=low · red=almost-gone · ring=out) ---- */
  function statusClass(status){
    return ({ 'in-stock':'s-status--in', 'low-inventory':'s-status--low', 'almost-gone':'s-status--gone', 'out-of-stock':'s-status--out' })[status] || '';
  }
  function statusTitle(status, noRestock){
    if (status === 'out-of-stock') return noRestock ? 'Sold out — notify me' : 'Pre-order';
    return ({ 'in-stock':'In stock', 'low-inventory':'Low inventory', 'almost-gone':'Almost gone — order soon' })[status] || '';
  }
  function paintSizeDots(){
    var dots = root.querySelectorAll('.size .s-status');
    Array.prototype.forEach.call(dots, function(dot){
      var id = dot.getAttribute('data-variant-id'); if(!id) return;
      var btn = dot.closest('.size');
      var noRestock = !!(btn && btn.getAttribute('data-no-restock') === 'true');
      fetchStatus(id).then(function(data){
        if(!data || !data.status) return;
        dot.className = 's-status ' + statusClass(data.status);
        dot.setAttribute('title', statusTitle(data.status, noRestock));
        if(btn) btn.setAttribute('data-stock', data.status);
      });
    });
  }
  paintSizeDots();
})();
